import React from 'react';
import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';

/**
 * Captura errores de render para evitar la pantalla en blanco.
 *
 * Sin este componente, una excepción durante el render (por ejemplo un dato
 * inesperado de Firestore) desmonta todo el árbol de React y deja la app
 * inutilizable, sin mensaje ni forma de recuperarse salvo recargar a mano.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Sin datos personales: solo el error y la pila del componente.
    console.error('[ErrorBoundary] Error de render:', error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
        gap: 16,
        color: 'var(--color-text)'
      }}>
        <div style={{
          background: 'var(--color-danger-light, rgba(231,76,60,0.1))',
          color: 'var(--color-danger, #e74c3c)',
          padding: 16,
          borderRadius: '50%',
          display: 'flex'
        }}>
          <FiAlertTriangle size={36} />
        </div>

        <h2 style={{ margin: 0, fontSize: '1.25rem' }}>
          Algo salió mal en esta pantalla
        </h2>

        <p style={{ margin: 0, maxWidth: 420, color: 'var(--color-secondary)', fontSize: '0.95rem', lineHeight: 1.5 }}>
          La aplicación encontró un problema al mostrar esta sección. Puedes intentar
          volver a cargarla o recargar la página. El resto de la aplicación sigue funcionando.
        </p>

        {this.props.showDetails && this.state.error && (
          <pre style={{
            maxWidth: '90vw',
            overflow: 'auto',
            fontSize: '0.75rem',
            textAlign: 'left',
            padding: 12,
            borderRadius: 8,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-secondary)'
          }}>
            {String(this.state.error?.message || this.state.error)}
          </pre>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={this.handleRetry}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              background: 'transparent',
              color: 'var(--color-text)',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600
            }}
          >
            Reintentar
          </button>
          <button
            onClick={this.handleReload}
            className="success"
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FiRefreshCw size={16} /> Recargar página
          </button>
        </div>
      </div>
    );
  }
}
